/**
 * Bulk Tag Editor
 * Full tag editor for multiple drills at once
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Save, Tag as TagIcon } from 'lucide-react';
import { UnifiedTag } from './UnifiedTag';
import { Drill } from './types/drill-types';
import { loadTaxonomy } from '@/lib/taxonomy-loader';

type TagMap = { [key: string]: string[] };

interface BulkTagEditorProps {
  drills: Drill[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (drillIds: string[], updatedTags: TagMap) => Promise<void>;
}
import logger from '@/lib/logging';

const normalizeTags = (tags: { [key: string]: string | string[] } = {}): TagMap => {
  const normalized: TagMap = {};
  Object.entries(tags).forEach(([category, value]) => {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    if (values.length > 0) {
      normalized[category] = Array.from(new Set(values));
    }
  });
  return normalized;
};

// Get tags that are common to ALL drills (intersection)
// This shows what tags all selected drills share
export const getCommonTags = (drills: Drill[]): TagMap => {
  if (drills.length === 0) return {};
  
  // Start with first drill's tags
  const firstDrillTags = normalizeTags(drills[0].tags);
  const common: TagMap = {};
  
  // For each category in the first drill
  Object.entries(firstDrillTags).forEach(([category, values]) => {
    // Check if all other drills have the same values for this category
    const allHaveCategory = drills.every(drill => {
      const drillTags = normalizeTags(drill.tags);
      const drillValues = drillTags[category] || [];
      // Check if all values from first drill exist in this drill
      return values.every(val => drillValues.includes(val));
    });
    
    if (allHaveCategory && values.length > 0) {
      common[category] = [...values].sort();
    }
  });
  
  return common;
};

export function BulkTagEditor({ drills, isOpen, onClose, onSave }: BulkTagEditorProps) {
  const [editedTags, setEditedTags] = useState<TagMap>(() => getCommonTags(drills));
  const [taxonomy, setTaxonomy] = useState<{ [key: string]: { type: string; options: string[] } }>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const initializeTaxonomy = async () => {
      const data = await loadTaxonomy();
      if (data.categories) {
        setTaxonomy(data.categories);
      }
    };
    initializeTaxonomy();
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Start with tags that are common to all drills
      // User can then add/remove tags which will be applied to all
      setEditedTags(getCommonTags(drills));
    }
  }, [drills, isOpen]);

  const handleSave = async () => {
    if (drills.length === 0) return;
    
    try {
      setIsSaving(true);
      const drillIds = drills.map(d => d.id);
      await onSave(drillIds, editedTags);
      onClose();
    } catch (error) {
      logger.error('Error saving bulk tags:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleTag = (category: string, option: string) => {
    const currentValues = editedTags[category] || [];
    const isSelected = currentValues.includes(option);
    const nextValues = isSelected
      ? currentValues.filter(v => v !== option)
      : [...currentValues, option];

    if (nextValues.length === 0) {
      const newTags = { ...editedTags };
      delete newTags[category];
      setEditedTags(newTags);
    } else {
      setEditedTags({
        ...editedTags,
        [category]: Array.from(new Set(nextValues)).sort()
      });
    }
  };

  const handleRemoveTag = (category: string, optionToRemove?: string) => {
    const currentValue = editedTags[category] || [];

    if (!optionToRemove) {
      // Remove entire category
      const newTags = { ...editedTags };
      delete newTags[category];
      setEditedTags(newTags);
    } else {
      // Remove specific option from multi-select
      const newValues = currentValue.filter(v => v !== optionToRemove);
      if (newValues.length === 0) {
        const newTags = { ...editedTags };
        delete newTags[category];
        setEditedTags(newTags);
      } else {
        setEditedTags({
          ...editedTags,
          [category]: Array.from(new Set(newValues)).sort()
        });
      }
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-bg/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 flex-col">
      <Card className="w-full max-w-2xl flex flex-col gap-0" style={{ maxHeight: '85vh' }}>
        <CardHeader className="pb-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TagIcon className="w-5 h-5" />
              <CardTitle className="text-lg text-accent">
                Edit Tags - {drills.length} drill{drills.length !== 1 ? 's' : ''}
              </CardTitle>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} size="sm" className="h-9" disabled={isSaving}>
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button onClick={onClose} variant="ghost" size="sm" className="h-9" disabled={isSaving}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-6 flex flex-col">
          <ScrollArea className="flex-1 w-full">
            <div className="space-y-6 pr-4">
              {/* Current Tags Display */}
              {Object.keys(editedTags).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Selected Tags</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-surface/30 rounded-lg border border-line">
                    {Object.entries(editedTags).map(([category, values]) => {
                      return values.map((val, idx) => (
                        <UnifiedTag
                          key={`${category}-${val}-${idx}`}
                          category={category}
                          value={val}
                          onClick={() => handleRemoveTag(category, val)}
                          size="sm"
                        />
                      ));
                    })}
                  </div>
                </div>
              )}

              {/* Tag Editors by Category */}
              <div className="space-y-4">
                <Label className="text-sm font-semibold">Add/Modify Common Tags</Label>
                <p className="text-xs text-muted">
                  Only tags that are common to all {drills.length} selected drill{drills.length !== 1 ? 's' : ''} are shown here. 
                  Changes will be applied to all drills. Tags unique to individual drills will remain unchanged.
                </p>
                {Object.entries(taxonomy).map(([category, config]) => {
                  if (config.type === 'text') return null;

                  const selectedValues = editedTags[category] || [];

                  return (
                    <div key={category} className="space-y-2 p-4 bg-surface/20 border border-line rounded-lg">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">{category}</Label>
                        {selectedValues.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTag(category)}
                            className="h-7 text-xs"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 p-2 bg-bg rounded border border-line">
                        {config.options && config.options.length > 0 ? (
                          config.options.map(option => {
                            const isSelected = selectedValues.includes(option);

                            return (
                              <UnifiedTag
                                key={option}
                                category={category}
                                value={option}
                                onClick={() => {
                                    handleToggleTag(category, option);
                                }}
                                isActive={isSelected}
                                size="sm"
                              />
                            );
                          })
                        ) : (
                          <p className="text-xs text-muted">No options available</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}

